/**
 * PORTED — RO-3-00g / #44474.
 * Origin: `excellent/app/_modes/new-goal-dialog.tsx`.
 *
 * Changes:
 *   `useRouter` from next/navigation → `useNavigate` from @excellent/ui/router
 *   `createGoalAction` → `apiCall(agentGoals.create)` (RO-2-09 / agent-goals)
 *   `createObjectiveAction` / `createKeyResultAction` → `apiCall(objectives.*)`
 */

import { useEffect, useState, useTransition } from "react";
import { useNavigate } from "@excellent/ui/router";
import {
  Button,
  CreateComposer,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@excellent/ui";
import { Bot, Plus, Target, TrendingUp } from "@excellent/ui/icons";
import { apiCall, useApi } from "@excellent/ui/api";
import { objectives as objectivesContract } from "@excellent/ui/api/contracts/objectives";
import { agentGoals } from "@excellent/ui/api/contracts/agent-goals";

/**
 * The header "New goal" modal — the ONE goal-creation entry point on the Goals
 * home (Work ▸ Goals + the Agents-side lens). It replaces the three ledger-bar
 * affordances (New strategic goal · Reconcile · +New goal link) with a single
 * PageHeader button that opens a kind-switchable form on the shared Linear-style
 * `CreateComposer` chrome:
 *
 *   • Agentic (default) — fleet-pursued durable intent. Same fields + validation
 *     as /goals/new, but submitted imperatively via `createGoalAction` (no
 *     navigation — the list refreshes in place).
 *   • Strategic — a metric with a committed target (the former Objective). Only
 *     offered on the canonical Work ▸ Goals home (`allowStrategic`); the
 *     Agents-side lens stays a projection, not a strategic editor (nav D3).
 *
 * The goal title is the composer's borderless headline; the kind switcher is the
 * masthead breadcrumb. A goal is a rich form, so the remaining properties keep
 * the label-led `FormField` layout (FORM-SYSTEM) inside the composer body.
 *
 * `roles` are passed from the server (listSpawnableRoles) since there's no roles
 * API; the strategic linked-goal options are self-fetched from
 * `objectives.summary` (the same edge NewStrategicGoalButton used).
 */

type GoalKind = "agentic" | "strategic";

const AGENTIC_ERROR_LABELS: Record<string, string> = {
  "title-required": "Title is required.",
  "unknown-role": "Pick a role from the list — those are the only authored roles.",
  "project-not-found": "That project slug doesn't exist.",
};

/** Radix Select can't carry an empty-string value, so "no linked goal" is a sentinel. */
const NO_GOAL = "__none__";

export function NewGoalDialog({
  roles,
  allowStrategic = false,
}: {
  roles: { name: string; description: string }[];
  /** Offer the Strategic kind (canonical Work ▸ Goals only). */
  allowStrategic?: boolean;
}) {
  const router = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const [err, setErr] = useState<string | null>(null);
  const [kind, setKind] = useState<GoalKind>("agentic");

  // Agentic fields (mirror /goals/new).
  const defaultRole = roles.some((r) => r.name === "shipper")
    ? "shipper"
    : (roles[0]?.name ?? "shipper");
  const [title, setTitle] = useState("");
  const [outcome, setOutcome] = useState("");
  const [why, setWhy] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState("on-demand");
  const [project, setProject] = useState("");

  // Strategic fields (mirror ObjectiveFormDialog create).
  const [sTitle, setSTitle] = useState("");
  const [sWhy, setSWhy] = useState("");
  const [period, setPeriod] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [goalUid, setGoalUid] = useState("");
  // The FIRST key result, collected at creation. Every strategic goal in the
  // workspace had zero KRs because nothing ever asked for one — so the band was
  // permanently "pending" and the row had nothing to measure. Asking here is
  // what makes a strategic goal born measurable instead of retro-fitted.
  const [krTitle, setKrTitle] = useState("");
  const [krStart, setKrStart] = useState("");
  const [krTarget, setKrTarget] = useState("");

  const activeTitle = kind === "strategic" ? sTitle : title;
  // An agentic goal MUST state its outcome. Every goal in the workspace predating
  // this had `outcome = NULL` — the field was optional here and only writable from
  // a separate /define page nothing routed you to, so "· no outcome" degraded from
  // a signal into wallpaper on every row. A strategic goal's title IS its outcome
  // (it's measured by key results), so it keeps the single-field form.
  const canSubmit =
    activeTitle.trim().length > 0 &&
    (kind === "strategic" || outcome.trim().length > 0) &&
    !pending;

  function reset() {
    setErr(null);
    setKind("agentic");
    setTitle("");
    setOutcome("");
    setWhy("");
    setRole(defaultRole);
    setDescription("");
    setCadence("on-demand");
    setProject("");
    setSTitle("");
    setSWhy("");
    setPeriod("");
    setDueOn("");
    setGoalUid("");
    setKrTitle("");
    setKrStart("");
    setKrTarget("");
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  // The per-section `+` in the goals ledger (packages/ui, mounted from server
  // components) can't hand us a callback, so it dispatches `excellent:new-goal`
  // with the section's kind. Same bus as `excellent:open-chat`. One modal, two
  // entry points — the header button and the ledger's `+`.
  useEffect(() => {
    function onNewGoal(e: Event) {
      const kind = (e as CustomEvent<{ kind?: GoalKind }>).detail?.kind;
      setErr(null);
      setKind(kind === "strategic" && allowStrategic ? "strategic" : "agentic");
      setOpen(true);
    }
    window.addEventListener("excellent:new-goal", onNewGoal);
    return () => window.removeEventListener("excellent:new-goal", onNewGoal);
  }, [allowStrategic]);

  function submit() {
    setErr(null);
    start(async () => {
      if (kind === "strategic") {
        const res = await apiCall(objectivesContract.create, {
          body: {
            title: sTitle,
            description: sWhy,
            period,
            dueOn,
            goalUid,
          },
        });
        if (res.ok) {
          // Born measurable: the first key result lands with the goal, so its
          // band and its progress track have something to read from the moment
          // it appears in the ledger. A failure here must not lose the goal —
          // it's already created, so surface the KR error and keep the modal.
          if (res.uid && krTitle.trim()) {
            const kr = await apiCall(objectivesContract.createKeyResult, {
              body: {
                objectiveUid: res.uid,
                title: krTitle,
                startValue: krStart,
                currentValue: krStart,
                targetValue: krTarget,
              },
            });
            if (!kr.ok) {
              setErr(
                `Goal created, but the key result didn’t save: ${kr.reason ?? "unknown error"}`,
              );
              router.refresh();
              return;
            }
          }
          onOpenChange(false);
          router.refresh();
        } else {
          setErr(res.reason ?? "Couldn’t create the goal.");
        }
        return;
      }
      const res = await apiCall(agentGoals.create, {
        body: {
          title,
          outcome,
          why,
          role,
          cadence,
          project,
          description,
        },
      });
      if (res.ok) {
        onOpenChange(false);
        router.refresh();
      } else {
        setErr(AGENTIC_ERROR_LABELS[res.reason] ?? res.reason ?? "Couldn’t create the goal.");
      }
    });
  }

  // The kind switcher is the masthead breadcrumb (when strategic is offered);
  // otherwise a static "Agentic" chip states the one kind this lens creates.
  const kindSwitcher = allowStrategic ? (
    <Select
      value={kind}
      onValueChange={(v) => {
        setErr(null);
        setKind(v as GoalKind);
      }}
    >
      <SelectTrigger variant="pill" size="sm" aria-label="Goal type" className="gap-1.5">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="agentic">
          <Bot />
          Agentic
        </SelectItem>
        <SelectItem value="strategic">
          <TrendingUp />
          Strategic
        </SelectItem>
      </SelectContent>
    </Select>
  ) : undefined;

  return (
    <>
      {/* Bare "+" create button — one grammar app-wide (Matt, 2026-07-23: "a +
          new button should always just be a +, not 'New goal'"). */}
      <Button
        size="icon-tb"
        aria-label="New goal"
        title="New goal"
        onClick={() => setOpen(true)}
      >
        <Plus className="size-4" />
      </Button>
      <CreateComposer
        open={open}
        onOpenChange={onOpenChange}
        context={kindSwitcher}
        contextLabel={allowStrategic ? undefined : "Agentic"}
        contextIcon={allowStrategic ? undefined : <Target />}
        heading="New goal"
        title={activeTitle}
        onTitleChange={kind === "strategic" ? setSTitle : setTitle}
        titlePlaceholder={
          kind === "strategic" ? "Reach $10k MRR" : "Clear evidence decisions weekly"
        }
        submitLabel="Create goal"
        onSubmit={submit}
        canSubmit={canSubmit}
        pending={pending}
        error={err}
      >
        <div className="flex flex-col gap-4">
          {kind === "strategic" ? (
            <StrategicFields
              why={sWhy}
              setWhy={setSWhy}
              period={period}
              setPeriod={setPeriod}
              dueOn={dueOn}
              setDueOn={setDueOn}
              goalUid={goalUid}
              setGoalUid={setGoalUid}
              krTitle={krTitle}
              setKrTitle={setKrTitle}
              krStart={krStart}
              setKrStart={setKrStart}
              krTarget={krTarget}
              setKrTarget={setKrTarget}
            />
          ) : (
            <AgenticFields
              outcome={outcome}
              setOutcome={setOutcome}
              why={why}
              setWhy={setWhy}
              role={role}
              setRole={setRole}
              roles={roles}
              description={description}
              setDescription={setDescription}
              cadence={cadence}
              setCadence={setCadence}
              project={project}
              setProject={setProject}
            />
          )}
        </div>
      </CreateComposer>
    </>
  );
}

function AgenticFields(props: {
  outcome: string;
  setOutcome: (v: string) => void;
  why: string;
  setWhy: (v: string) => void;
  role: string;
  setRole: (v: string) => void;
  roles: { name: string; description: string }[];
  description: string;
  setDescription: (v: string) => void;
  cadence: string;
  setCadence: (v: string) => void;
  project: string;
  setProject: (v: string) => void;
}) {
  return (
    <>
      <FormField
        label="Outcome"
        required
        htmlFor="goal-outcome"
        help="One sentence — what's true when this is done. Add the metric / checklist finish line after creating."
      >
        <Textarea
          id="goal-outcome"
          autosize
          rows={1}
          value={props.outcome}
          onChange={(e) => props.setOutcome(e.target.value)}
          placeholder="New workspaces reach first real value in under 5 minutes."
        />
      </FormField>

      <FormField label="Why it matters" htmlFor="goal-why">
        <Textarea
          id="goal-why"
          autosize
          rows={1}
          value={props.why}
          onChange={(e) => props.setWhy(e.target.value)}
          placeholder="The stakes — what this unlocks, or the problem it solves."
        />
      </FormField>

      <FormField
        label="Role"
        htmlFor="goal-role"
        help={props.roles.length === 0 ? undefined : "Only authored roles can run."}
        error={
          props.roles.length === 0
            ? "No roles found in packages/agents/roles/."
            : undefined
        }
      >
        <Select value={props.role} onValueChange={props.setRole}>
          <SelectTrigger id="goal-role">
            <SelectValue placeholder="Pick a role" />
          </SelectTrigger>
          <SelectContent>
            {props.roles.map((r) => (
              <SelectItem key={r.name} value={r.name}>
                {r.name} — {r.description.slice(0, 80)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField label="Description" htmlFor="goal-description">
        <Textarea
          id="goal-description"
          autosize
          rows={1}
          value={props.description}
          onChange={(e) => props.setDescription(e.target.value)}
          placeholder="What success looks like, what's in/out of scope, and any constraints."
        />
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField
          label="Cadence"
          htmlFor="goal-cadence"
          help="on-demand · every 5m · daily HH:MM"
        >
          <Input
            id="goal-cadence"
            value={props.cadence}
            onChange={(e) => props.setCadence(e.target.value)}
            placeholder="on-demand"
          />
        </FormField>
        <FormField
          label="Project"
          htmlFor="goal-project"
          help="Scopes the goal's issues to this project."
        >
          <Input
            id="goal-project"
            value={props.project}
            onChange={(e) => props.setProject(e.target.value)}
            placeholder="project-slug"
          />
        </FormField>
      </div>
    </>
  );
}

function StrategicFields(props: {
  why: string;
  setWhy: (v: string) => void;
  period: string;
  setPeriod: (v: string) => void;
  dueOn: string;
  setDueOn: (v: string) => void;
  goalUid: string;
  setGoalUid: (v: string) => void;
  krTitle: string;
  setKrTitle: (v: string) => void;
  krStart: string;
  setKrStart: (v: string) => void;
  krTarget: string;
  setKrTarget: (v: string) => void;
}) {
  // The agentic goals a strategic goal can link (never forks). Fetched HERE so
  // the query only fires when the user actually switches to Strategic — the
  // agentic lens (no strategic kind) never pays for it. Same edge the retired
  // NewStrategicGoalButton used (objectives.summary).
  const { data: summary } = useApi(
    objectivesContract.summary,
    { query: {} },
    { keepPreviousData: true },
  );
  const goalOptions = summary?.goalOptions ?? [];
  return (
    <>
      <FormField label="Why it matters" htmlFor="sg-why">
        <Textarea
          id="sg-why"
          autosize
          rows={1}
          value={props.why}
          onChange={(e) => props.setWhy(e.target.value)}
          placeholder="The stakes — what this unlocks, or the problem it solves."
        />
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Period" htmlFor="sg-period" help="e.g. 2026-Q3">
          <Input
            id="sg-period"
            value={props.period}
            onChange={(e) => props.setPeriod(e.target.value)}
            placeholder="2026-Q3"
          />
        </FormField>
        <FormField label="Due" htmlFor="sg-due">
          <Input
            id="sg-due"
            type="date"
            value={props.dueOn}
            onChange={(e) => props.setDueOn(e.target.value)}
          />
        </FormField>
      </div>

      {/* The first key result. Optional, but asked for up front: a strategic
          goal with none is unmeasurable — its band sits at "pending" forever
          and its ledger row has an empty progress track. */}
      <FormField
        label="First key result"
        htmlFor="sg-kr"
        help="What number says this is working. You can add more on the goal page."
      >
        <Input
          id="sg-kr"
          value={props.krTitle}
          onChange={(e) => props.setKrTitle(e.target.value)}
          placeholder="Monthly recurring revenue"
        />
      </FormField>

      {props.krTitle.trim() ? (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Starting at" htmlFor="sg-kr-start">
            <Input
              id="sg-kr-start"
              type="number"
              value={props.krStart}
              onChange={(e) => props.setKrStart(e.target.value)}
              placeholder="0"
            />
          </FormField>
          <FormField label="Target" htmlFor="sg-kr-target">
            <Input
              id="sg-kr-target"
              type="number"
              value={props.krTarget}
              onChange={(e) => props.setKrTarget(e.target.value)}
              placeholder="10000"
            />
          </FormField>
        </div>
      ) : null}

      <FormField
        label="Agentic goal"
        htmlFor="sg-goal"
        help="The executable goal this strategic goal drives — it links the goal, never forks it."
      >
        <Select
          value={props.goalUid || NO_GOAL}
          onValueChange={(v) => props.setGoalUid(v === NO_GOAL ? "" : v)}
        >
          <SelectTrigger id="sg-goal">
            <SelectValue placeholder="No linked goal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_GOAL}>No linked goal</SelectItem>
            {goalOptions.map((g) => (
              <SelectItem key={g.uid} value={g.uid}>
                {g.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    </>
  );
}
